import { useMemo } from 'react';
import { useGetUserMandates } from '../../../common/api/endpoints/users/users';
import { useAuthStore } from '../../../common/auth/store';

/**
 * Lightweight calendar badge data for persistent lecturer shell consumers.
 * Fetches mandates only — no slots or event-task list.
 */
export const useCalendarBadges = () => {
  const userId = useAuthStore((state) => state.user.id);

  const { data: mandatesData, isPending: tasksLoading } = useGetUserMandates(userId, {
    query: {
      enabled: !!userId,
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
    },
  });

  const mandates = useMemo(() => mandatesData?.data?.data || [], [mandatesData]);

  const moduleMap = useMemo(() => {
    const map = {};

    mandates.forEach((m) => {
      if (m.moduleId && m.moduleTitle) {
        map[m.moduleId] = m.moduleTitle;
      }
    });

    return map;
  }, [mandates]);

  const planningGaps = useMemo(() => {
    if (!mandates.length) return [];

    return mandates
      .filter((m) => m.eventKindId)
      .map((m) => {
        const hasGap = m.planningStatus === 'minor' || m.planningStatus === 'critical';

        if (!hasGap) return null;

        return {
          moduleId: m.moduleId,
          moduleTitle: m.moduleTitle || moduleMap[m.moduleId] || m.moduleId,
          eventKindId: m.eventKindId,
          severity: m.planningStatus,
          reason: m.planningReason,
          missingCount: m.missingCount || 0,
          segments: m.segments || [],
        };
      })
      .filter(Boolean);
  }, [mandates, moduleMap]);

  const totalMissingSlots = useMemo(
    () => planningGaps.reduce((acc, gap) => acc + (gap.missingCount || 0), 0),
    [planningGaps],
  );

  const hasEventTasks = useMemo(
    () => mandates.some((m) => !!m.eventKindId),
    [mandates],
  );
  const gapCount = totalMissingSlots || 0;
  const hasCriticalGaps = planningGaps?.some((o) => o.severity === 'critical');

  return {
    mandates,
    moduleMap,
    planningGaps,
    totalMissingSlots,
    tasksLoading,
    hasEventTasks,
    gapCount,
    hasCriticalGaps,
  };
};
